import { VirtualDelete } from '@eagle/api-types';
import { Alert, ListItem, Stack, Typography, styled } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CALL_TEXT_LENGTH } from '../../constants';
import { trackEvent } from '../../util';
import { ListPaper } from '../list-item';
import { MiddleSpinner } from '../middle-spinner';
import { BasicSearchResultsProps, SearchResultsProps } from './types';

const BasicSearchResultItem = styled(ListItem)({
  padding: 0,
  userSelect: 'none',
});

/**
  * FUNCTION: Display an search result Item
  */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function SearchResults<T>({
  buildHref,
  getItemTestId,
  handleFormatListItem,
  highlightIndex,
  id,
  isLoading,
  noResultsInstructions,
  searchResults,
  selectedItem,
  text,
  disableAction,
  disableRipple,
  ...props
}: SearchResultsProps<T>): JSX.Element {
  const { t } = useTranslation(['common']);

  if (text && text.length < API_CALL_TEXT_LENGTH) {
    return <Box my={0}><Alert data-testid="no-search-results-min-chars" severity="info">{t('common:component.search.hint.less-than-count', { count: API_CALL_TEXT_LENGTH })}</Alert></Box>;
  }
  if (!searchResults) {
    return isLoading ? <MiddleSpinner /> : <Box my={3}><Alert data-testid="search-error" severity="error">{t('common:component.search.hint.unexpected-error-message')}</Alert></Box>;
  }
  if (!searchResults.length) {
    return <Box my={0}><Alert data-testid="no-search-results" severity="info">{noResultsInstructions}</Alert></Box>;
  }

  return (
    <Stack data-testid={props['data-testid']} spacing={2} style={{ filter: isLoading ? 'blur(1px)' : '', opacity: isLoading ? 0.66 : 1 }}>
      {searchResults.map((item, index) => (
        <ListPaper
          data-testid={getItemTestId ? getItemTestId(item) : `item-${index}`}
          key={index}
          elevation={index === highlightIndex ? 5 : 1}
          href={!disableAction && buildHref ? buildHref(item) : undefined}
          selected={item === selectedItem}
          deleted={Boolean((item as VirtualDelete).deleted)}
          disableAction={disableAction}
          disableRipple={disableRipple}
          onClick={() => {
            if (id) {
              trackEvent('list', 'selected_item', `${id}_list`, { 'selected_item': index ? (index + 1) : 1, 'item_link': buildHref ? buildHref(item) : '' });
            }
          }}
        >
          {handleFormatListItem(item)}
        </ListPaper>
      ))}
    </Stack>
  );
}

/**
  * FUNCTION: Display a basic search result Item
  */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function SearchResultsBasic<T>({
  buildHref,
  handleFormatListItem,
  initialInstructions,
  isLoading,
  noResultsInstructions,
  searchResults,
  text,
  disableAction,
  ...props
}: BasicSearchResultsProps<T>): JSX.Element {
  const { t } = useTranslation(['common']);

  if (text && text.length < API_CALL_TEXT_LENGTH) {
    return <Box my={0}><Alert data-testid="no-search-results-min-chars" severity="info">{t('common:component.search.hint.less-than-count', { count: API_CALL_TEXT_LENGTH })}</Alert></Box>;
  }
  if (!searchResults) {
    return <Typography data-testid="no-search-results" color="text.secondary" my={0}>{initialInstructions}</Typography>;
  }
  if (!searchResults.length) {
    return <Box my={3}><Alert data-testid="no-search-results" severity="info">{noResultsInstructions}</Alert></Box>;
  }

  return (
    <Stack data-testid={props['data-testid']} spacing={2} style={{ filter: isLoading ? 'blur(1px)' : '', opacity: isLoading ? 0.66 : 1 }}>
      {searchResults.map((item, index) => (
        <ListPaper
          data-testid={`item-${index}`}
          key={index}
          elevation={1}
          href={!disableAction && buildHref ? buildHref(item) : undefined}
          deleted={Boolean((item as VirtualDelete).deleted)}
          disableAction={disableAction}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          <BasicSearchResultItem>
            {handleFormatListItem(item)}
          </BasicSearchResultItem>
        </ListPaper>
      ))}
    </Stack>
  );
}
